import { useState, useEffect, useMemo } from 'react';
import api from './api/axiosInstance';
import Sidebar from './Sidebar.js';
import Breadcrumb from './Breadcrumb';
import { Container, Row, Col, Card, Form, Table, Button, Badge } from 'react-bootstrap';
import { exportJobsReportPdf } from './api/exportJobsReportPdf.js';

const formatUpdatedAt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ReportingJobsList = () => {
    const [tickets, setTickets] = useState([]);
    const [jobUpdates, setJobUpdates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [countryFilter, setCountryFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [updatedByFilter, setUpdatedByFilter] = useState('');
    const [fieldFilter, setFieldFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    useEffect(() => {
        const fetchTickets = async () => {
            try {
                const res = await api.get('/api/tickets');
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }
        };
        fetchTickets();
    }, []);

    useEffect(() => {
        const fetchJobUpdates = async () => {
            setIsLoading(true);
            try {
                const res = await api.get('/api/reporting/jobs');
                setJobUpdates(Array.isArray(res.data) ? res.data : []);
            } catch (error) {
                console.error('Error fetching job updates report:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchJobUpdates();
    }, []);

    const countryOptions = useMemo(
        () => [...new Set(jobUpdates.map((row) => row.country).filter(Boolean))].sort(),
        [jobUpdates]
    );
    const companyOptions = useMemo(
        () => [...new Set(jobUpdates.map((row) => row.companyName).filter(Boolean))].sort(),
        [jobUpdates]
    );
    const updatedByOptions = useMemo(
        () => [...new Set(jobUpdates.map((row) => row.updatedByName).filter(Boolean))].sort(),
        [jobUpdates]
    );
    const fieldOptions = useMemo(
        () => [...new Set(jobUpdates.map((row) => row.fieldLabel).filter(Boolean))].sort(),
        [jobUpdates]
    );

    const filteredRows = useMemo(() => {
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;
        if (to) to.setHours(23, 59, 59, 999);

        return jobUpdates.filter((row) => {
            if (countryFilter && row.country !== countryFilter) return false;
            if (companyFilter && row.companyName !== companyFilter) return false;
            if (updatedByFilter && row.updatedByName !== updatedByFilter) return false;
            if (fieldFilter && row.fieldLabel !== fieldFilter) return false;

            const updatedAt = row.updatedAt ? new Date(row.updatedAt) : null;
            if (from && (!updatedAt || updatedAt < from)) return false;
            if (to && (!updatedAt || updatedAt > to)) return false;

            return true;
        });
    }, [jobUpdates, countryFilter, companyFilter, updatedByFilter, fieldFilter, dateFrom, dateTo]);

    const handleClearFilters = () => {
        setCountryFilter('');
        setCompanyFilter('');
        setUpdatedByFilter('');
        setFieldFilter('');
        setDateFrom('');
        setDateTo('');
    };

    const hasActiveFilters = Boolean(
        countryFilter || companyFilter || updatedByFilter || fieldFilter || dateFrom || dateTo
    );

    const handleDownloadReport = async () => {
        try {
            await exportJobsReportPdf(filteredRows, {
                country: countryFilter,
                company: companyFilter,
                updatedBy: updatedByFilter,
                dateFrom,
                dateTo,
                fieldLabel: fieldFilter,
            });
        } catch (error) {
            console.error('Error exporting job updates report PDF:', error);
            alert('An error occurred while exporting the job updates report PDF.');
        }
    };

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body>
                                    <Row>
                                        <Col xs={12}>
                                            <h1 className="mb-4">Job Updates Report</h1>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group controlId="reportFilterCountry" className="mb-3">
                                                <Form.Label>Country</Form.Label>
                                                <Form.Select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
                                                    <option value="">All countries</option>
                                                    {countryOptions.map((country) => (
                                                        <option key={country} value={country}>{country}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group controlId="reportFilterCompany" className="mb-3">
                                                <Form.Label>Company</Form.Label>
                                                <Form.Select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}>
                                                    <option value="">All companies</option>
                                                    {companyOptions.map((company) => (
                                                        <option key={company} value={company}>{company}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group controlId="reportFilterUpdatedBy" className="mb-3">
                                                <Form.Label>Updated by</Form.Label>
                                                <Form.Select value={updatedByFilter} onChange={(e) => setUpdatedByFilter(e.target.value)}>
                                                    <option value="">All users</option>
                                                    {updatedByOptions.map((name) => (
                                                        <option key={name} value={name}>{name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="align-items-end">
                                        <Col md={3}>
                                            <Form.Group controlId="reportFilterDateFrom" className="mb-3">
                                                <Form.Label>Date from</Form.Label>
                                                <Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group controlId="reportFilterDateTo" className="mb-3">
                                                <Form.Label>Date to</Form.Label>
                                                <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group controlId="reportFilterField" className="mb-3">
                                                <Form.Label>Data point</Form.Label>
                                                <Form.Select value={fieldFilter} onChange={(e) => setFieldFilter(e.target.value)}>
                                                    <option value="">All data points</option>
                                                    {fieldOptions.map((field) => (
                                                        <option key={field} value={field}>{field}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3} className="mb-3 d-flex justify-content-end gap-2">
                                            <Button variant="link" onClick={handleClearFilters}>
                                                Clear filters
                                            </Button>
                                            <Button variant="outline-secondary" onClick={handleDownloadReport}>
                                                Download report
                                            </Button>
                                        </Col>
                                    </Row>
                                    {hasActiveFilters && (
                                        <Row className="filter-results">
                                            <Col className="d-flex align-items-center flex-wrap gap-2">
                                                <span>Filters:</span>

                                                {countryFilter && (
                                                    <Badge bg="info" className="me-1">
                                                        Country: {countryFilter}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setCountryFilter('')}>
                                                            ×
                                                        </Button>
                                                    </Badge>
                                                )}

                                                {companyFilter && (
                                                    <Badge bg="info" className="me-1">
                                                        Company: {companyFilter}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setCompanyFilter('')}>
                                                            ×
                                                        </Button>
                                                    </Badge>
                                                )}

                                                {updatedByFilter && (
                                                    <Badge bg="info" className="me-1">
                                                        Updated by: {updatedByFilter}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setUpdatedByFilter('')}>
                                                            ×
                                                        </Button>
                                                    </Badge>
                                                )}

                                                {fieldFilter && (
                                                    <Badge bg="info" className="me-1">
                                                        Data point: {fieldFilter}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setFieldFilter('')}>
                                                            ×
                                                        </Button>
                                                    </Badge>
                                                )}

                                                {dateFrom && (
                                                    <Badge bg="info" className="me-1">
                                                        Date from: {dateFrom}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setDateFrom('')}>
                                                            ×
                                                        </Button>
                                                    </Badge>
                                                )}

                                                {dateTo && (
                                                    <Badge bg="info" className="me-1">
                                                        Date to: {dateTo}{' '}
                                                        <Button variant="link" size="sm" onClick={() => setDateTo('')}>
                                                            ×
                                                        </Button>
                                                    </Badge>
                                                )}

                                                <Button variant="link" size="sm" className="ms-auto" onClick={handleClearFilters}>
                                                    Clear All
                                                </Button>
                                            </Col>
                                        </Row>
                                    )}
                                    <Row>
                                        <Col>
                                            <Table hover responsive className="mb-0">
                                                <thead>
                                                    <tr>
                                                        <th className="text-nowrap">Updated at</th>
                                                        <th className="text-nowrap">Country</th>
                                                        <th className="text-nowrap">Company</th>
                                                        <th className="text-nowrap">Updated by</th>
                                                        <th className="text-nowrap">Data point</th>
                                                        <th className="text-nowrap">Old value</th>
                                                        <th className="text-nowrap">New value</th>
                                                        <th>Change summary</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredRows.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={8} className="text-center text-muted">
                                                                No job updates match the selected filters.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        filteredRows.map((row) => (
                                                            <tr key={row.id}>
                                                                <td className="text-nowrap">{formatUpdatedAt(row.updatedAt)}</td>
                                                                <td className="text-nowrap">{row.country}</td>
                                                                <td className="text-nowrap">{row.companyName}</td>
                                                                <td className="text-nowrap">{row.updatedByName}</td>
                                                                <td className="text-nowrap">{row.fieldLabel}</td>
                                                                <td className="text-nowrap">{String(row.oldValue)}</td>
                                                                <td className="text-nowrap">{String(row.newValue)}</td>
                                                                <td style={{ whiteSpace: 'pre-wrap', minWidth: '220px' }}>{row.changeSummary}</td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};

export default ReportingJobsList;
